// Case8Page.js – MetroMove Transit
import React, { useEffect, useState, useRef } from 'react';
import { PencilIcon, BoltIcon, ChartBarIcon, LightBulbIcon } from '@heroicons/react/24/outline';
const case8Image = require('../assets/RedRockCases/Case8/case 8 placeholder.png');
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, ScatterChart, Scatter, ZAxis, LabelList } from 'recharts';
import SEO from '../components/SEO';

const TIMER_WARNING_THRESHOLD = 0.8; // 80% of the way through

const Case8Page = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1200);
  const [answers, setAnswers] = useState({});
  const [feedback, setFeedback] = useState({});
  const [reveal, setReveal] = useState({});
  const [timeExpired, setTimeExpired] = useState(false);
  const [finishedEarly, setFinishedEarly] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [notebookItems, setNotebookItems] = useState([]);
  const [isNotebookOpen, setIsNotebookOpen] = useState(true);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [currentExhibit, setCurrentExhibit] = useState('1');
  const timerRef = useRef(null);

  // Move dynamic scorecard variables to top-level scope
  const reportAnswers = {
    'report_district': 'Central',
    'report_commute': '41',
    'report_mode': 'Tram',
    'report_ontime': '90',
    'report_transport': 'Bus',
    'report_complaints': '45',
    'report_growth': '15',
    'report_district2': 'Central'
  };

  const questionData = [
    { 
      id: 1, 
      type: 'text', 
      question: 'Which district has the highest total transit usage across all modes?', 
      correctAnswer: 'Central' 
    },
    { 
      id: 2, 
      type: 'text',
      question: 'Which zone has the longest average commute time?',
      correctAnswer: 'Central'
    },
    { 
      id: 3, 
      type: 'text', 
      question: 'What percentage of complaints are related to bus services?',
      correctAnswer: '45%' 
    },
    { 
      id: 4, 
      type: 'text', 
      question: 'Which transit mode had the lowest on-time performance in February?', 
      correctAnswer: 'Light Rail' 
    },
    { 
      id: 5, 
      type: 'text',
      question: 'Among 18–24 year olds, the most preferred transit mode is what?',
      correctAnswer: 'Bus'
    },
    { 
      id: 6, 
      type: 'text', 
      question: 'Which age group prefers Light Rail the most?', 
      correctAnswer: '55+' 
    },
    { 
      id: 7, 
      type: 'text',
      question: 'Which district has the lowest projected ridership growth?',
      correctAnswer: 'South'
    },
    { 
      id: 8, 
      type: 'text', 
      question: 'How many total tram rides were recorded in Uptown?', 
      correctAnswer: '1,900' 
    }
  ];

  // Calculate dynamic score values
  const numReportQuestions = Object.keys(reportAnswers).length;
  const numAnalysisQuestions = questionData.length;
  const numReportCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && k.startsWith('report_')).length;
  const numAnalysisCorrect = Object.entries(feedback).filter(([k, v]) => v === 'correct' && !k.startsWith('report_')).length;
  const totalQuestions = numReportQuestions + numAnalysisQuestions;
  const totalCorrect = numReportCorrect + numAnalysisCorrect;

  useEffect(() => {
    if (!hasStarted || timeExpired || finishedEarly) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setTimeExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [hasStarted, timeExpired, finishedEarly]);

  useEffect(() => {
    // Check if all questions are answered
    const allAnswered = hasStarted && !isComplete && Object.keys(feedback).length === 8;
    
    if (allAnswered) {
      setIsComplete(true);
      // If completed and time is still left, mark as finished early
      if (timeLeft > 0) {
        setFinishedEarly(true);
        // Optional: Stop the timer if finished early
        // clearInterval(timerRef.current);
      }
    }
    // Added questionData.length, timeLeft, and setFinishedEarly to dependencies
  }, [feedback, hasStarted, isComplete, timeLeft, setFinishedEarly]);

  const handleInput = (id, value, partIndex = null) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    if (partIndex !== null) {
      setAnswers((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [partIndex]: value }
      }));
    } else {
    setAnswers((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handleSubmit = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const q = questionData.find((q) => q.id === id);
    
    if (q.type === 'multi') {
      const allPartsCorrect = q.parts.every((part, index) => {
        const userAnswer = ((answers[id] || {})[index] || '').trim().toLowerCase();
        return userAnswer === part.correctAnswer.toLowerCase();
      });
      setFeedback((prev) => ({ ...prev, [id]: allPartsCorrect ? 'correct' : 'incorrect' }));
    } else {
    const userAnswer = (answers[id] || '').trim().toLowerCase();
    const correctAnswer = q.correctAnswer.toLowerCase();
    setFeedback((prev) => ({ ...prev, [id]: userAnswer === correctAnswer ? 'correct' : 'incorrect' }));
    }
  };

  const handleTryAgain = (id) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: '' }));
    setFeedback((prev) => ({ ...prev, [id]: null }));
    setReveal((prev) => ({ ...prev, [id]: false }));
  };

  const handleReveal = (id) => {
    setReveal((prev) => ({ ...prev, [id]: true }));
  };

  const handleMCQSelect = (id, optionLabel) => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    setAnswers((prev) => ({ ...prev, [id]: optionLabel }));
    const q = questionData.find((q) => q.id === id);
    setFeedback((prev) => ({ ...prev, [id]: optionLabel === q.correctAnswer ? 'correct' : 'incorrect' }));
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleReportSubmit = () => {
    if (timeExpired || !hasStarted || finishedEarly) return;
    const newFeedback = {};
    Object.entries(reportAnswers).forEach(([key, correctAnswer]) => {
      const userAnswer = (answers[key] || '').trim();
      newFeedback[key] = userAnswer.toLowerCase() === correctAnswer.toLowerCase() ? 'correct' : 'incorrect';
    });
    setFeedback(prev => ({ ...prev, ...newFeedback, report: 'submitted' }));
    setFinishedEarly(true);
    setTimeExpired(true);
    setReveal(prev => ({ ...prev, report: true }));
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handleReportReveal = () => {
    setReveal(prev => ({ ...prev, report: true }));
  };

  const handleDragStart = (e, value, label) => {
    if (e.target.closest('.notebook-content')) return;
    const data = {
      value: value,
      label: label
    };
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  const handleDrop = (e) => {
    if (!hasStarted) return;
    e.preventDefault();
    try {
      const data = e.dataTransfer.getData('application/json');
      if (!data) return;
      
      const parsedData = JSON.parse(data);
      if (parsedData.value && parsedData.label) {
        setNotebookItems(prev => [...prev, parsedData]);
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
    e.currentTarget.classList.remove('bg-blue-50');
  };

  const removeNotebookItem = (index) => {
    setNotebookItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleNotebookToggle = () => {
    if (!isNotebookOpen) {
      setHasBeenOpened(true);
    }
    setIsNotebookOpen(!isNotebookOpen);
  };

  // Exhibit 1: Transit Usage by District and Mode
  const exhibit1Data = [
    { district: 'Central', Bus: 4100, Tram: 2200, LightRail: 3100 },
    { district: 'East', Bus: 2900, Tram: 1500, LightRail: 1800 },
    { district: 'West', Bus: 2700, Tram: 1800, LightRail: 2000 },
    { district: 'Uptown', Bus: 3300, Tram: 1900, LightRail: 1800 },
    { district: 'South', Bus: 2500, Tram: 1600, LightRail: 1700 },
    { district: 'North', Bus: 2600, Tram: 1700, LightRail: 1600 },
  ];
  // Exhibit 2: Average Commute Time by Zone
  const exhibit2Data = [
    { zone: 'Central', Minutes: 41 },
    { zone: 'East', Minutes: 33 },
    { zone: 'West', Minutes: 30 },
    { zone: 'Uptown', Minutes: 34 },
    { zone: 'South', Minutes: 36 },
    { zone: 'North', Minutes: 32 },
  ];
  // Exhibit 3: Transit Complaint Distribution by Mode
  const exhibit3Data = [
    { mode: 'Bus', value: 45 },
    { mode: 'Tram', value: 30 },
    { mode: 'Light Rail', value: 25 },
  ];
  const exhibit3Colors = ['#FFB300', '#FF6F00', '#F73378'];
  // Exhibit 4: On-Time Performance by Transit Mode
  const exhibit4Data = [
    { week: 'Week 1', Bus: 84, Tram: 91, LightRail: 86 },
    { week: 'Week 2', Bus: 82, Tram: 92, LightRail: 83 },
    { week: 'Week 3', Bus: 85, Tram: 93, LightRail: 81 },
    { week: 'Week 4', Bus: 83, Tram: 94, LightRail: 84 },
  ];
  // Exhibit 5: Transit Preference by Age Group
  const exhibit5Data = [
    { age: '18–24', Bus: 40, Tram: 20, LightRail: 10 },
    { age: '25–34', Bus: 35, Tram: 25, LightRail: 15 },
    { age: '35–44', Bus: 25, Tram: 30, LightRail: 25 },
    { age: '45–54', Bus: 20, Tram: 35, LightRail: 30 },
    { age: '55+', Bus: 10, Tram: 40, LightRail: 50 },
  ];
  // Exhibit 6: Projected Ridership Growth by District
  const exhibit6Data = [
    { district: 'Central', Growth: 15 },
    { district: 'East', Growth: 10 },
    { district: 'West', Growth: 11 },
    { district: 'Uptown', Growth: 13 },
    { district: 'South', Growth: 8 },
    { district: 'North', Growth: 12 },
  ];

  // For Exhibit 3: Custom tooltip for pie chart
  const exhibit3Tooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border rounded px-2 py-1 text-xs shadow">
          <span>{payload[0].name}: {payload[0].value}%</span>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <SEO noIndex={true} />
      <div className="bg-white text-gray-900 font-sans relative scroll-pt-[400px] scroll-smooth">
        {/* Mobile Warning - Shown on small screens */}
        <div className="md:hidden p-6 h-screen flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-20 h-20 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Desktop Required</h1>
            <p className="text-gray-700 max-w-sm mx-auto">
              Cases are designed for desktop and laptop computers. Please use a larger screen to access this case study.
            </p>
            <div className="pt-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg text-blue-700">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">Best viewed on screens wider than 768px</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Content - Hidden on mobile */}
        <div className="hidden md:block">
          {/* Side Navigation */}
          <div className="fixed right-6 top-[285px] flex flex-col gap-6 z-[9999]">
            <div className="group relative">
            <a 
              href="#problem" 
                className="flex items-center"
              aria-label="Problem Section"
            >
                <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Problem
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <PencilIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
            </div>

            <div className="group relative">
              <a 
                href="#investigation" 
                className="flex items-center"
                aria-label="Investigation Section"
              >
                <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                  Investigation
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <BoltIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
            </div>

            <div className="group relative">
              <a 
                href="#exhibits" 
                className="flex items-center"
                aria-label="Exhibits Section"
              >
                <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                  Exhibits
                </div>
                <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </a>
            </div>

            <div className="group relative">
              <a 
                href="#questions" 
                className="flex items-center"
                aria-label="Analysis Section"
              >
                <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                  Analysis
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <ChartBarIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
            </div>

            <div className="group relative">
            <a 
              href="#recommendation" 
              className="flex items-center"
              aria-label="Recommendation Section"
            >
                <div className="absolute right-full mr-2 px-2 py-1 rounded bg-white/80 backdrop-blur-sm text-sm text-gray-600 font-medium opacity-0 -translate-x-2 transition-all duration-200 whitespace-nowrap shadow-sm group-hover:opacity-100 group-hover:translate-x-0 z-[10000]">
                Recommendation
              </div>
              <div className="p-2 rounded-lg bg-white shadow-md hover:shadow-lg transition-shadow">
                <LightBulbIcon className="w-5 h-5 text-gray-400" />
              </div>
            </a>
            </div>
          </div>

          {/* Hero Section - Enhanced gradient */}
          <section className="sticky top-[50px] z-30 bg-gradient-to-br from-blue-200 to-white pb-4 relative">
            <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 pt-16">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-3">Case 8: MetroMove – City Transit Expansion Plan</h1>
                <p className="text-base md:text-lg text-gray-700">Analyze public transportation data to recommend zone prioritization and transit mode funding based on performance, coverage, and satisfaction metrics.</p>
                <div className="mt-4 mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-sm text-blue-700 flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    For the best experience, adjust your browser zoom to 75-80%. This ensures all case elements display properly and are easy to interact with.
                  </p>
                </div>
                {(!hasStarted || timeExpired || finishedEarly) && (
                  <button
                    onClick={() => {
                      setHasStarted(true);
                      setTimeLeft(1200);
                      setAnswers({});
                      setFeedback({});
                      setReveal({});
                      setTimeExpired(false);
                      setFinishedEarly(false);
                      setIsComplete(false);
                    }}
                    className="group relative mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                  >
                    {!hasStarted ? 'Start Case' : 'Restart Case'}
                    <div className="absolute top-full mt-2 left-0 bg-gray-800 text-white text-xs px-3 py-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-auto">
                      {!hasStarted ? 'Click to unlock the questions and start your case timer.' : 'Click to start a new attempt.'}
                    </div>
                  </button>
                )}
              </div>
            </div>
            <div className="absolute top-[85px] right-10 md:right-20 w-[300px] h-[400px] z-10 hidden md:block">
              {hasStarted && (timeExpired || isComplete) && (
                <div className={`absolute top-0 -left-[250px] bg-white/90 backdrop-blur-sm rounded-lg p-4 animate-pulse w-[220px] ${
                  Math.round((totalCorrect / totalQuestions) * 100) < 65 
                  ? 'shadow-[0_0_15px_rgba(239,68,68,0.2)]' 
                  : 'shadow-[0_0_15px_rgba(59,130,246,0.2)]'
                }`}>
                  <div className="relative">
                    <div className={`absolute inset-0 bg-gradient-to-r rounded-lg ${
                      Math.round((totalCorrect / totalQuestions) * 100) < 65
                      ? 'from-red-50/50 to-white/50'
                      : 'from-blue-50/50 to-white/50'
                    }`}></div>
                    <div className="relative space-y-2.5">
                      <h2 className="text-[16px] font-semibold text-gray-800 flex items-center gap-1.5">
                        <span className="text-lg">📊</span> Final Score
                      </h2>
                      <p className="text-[15px] text-gray-700">
                        Analysis: {numAnalysisCorrect}/{numAnalysisQuestions}
                      </p>
                      <p className="text-[15px] text-gray-700">
                        Report: {numReportCorrect}/{numReportQuestions}
                      </p>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div 
                          className={`rounded-full h-2 transition-all duration-300 ${
                            Math.round((totalCorrect / totalQuestions) * 100) < 65
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.round((totalCorrect / totalQuestions) * 100)}%` }}
                        ></div>
                      </div>
                      <p className={`text-[14px] ${
                        Math.round((totalCorrect / totalQuestions) * 100) < 65
                        ? 'text-red-600'
                        : 'text-gray-600'
                      }`}>
                        Total: {totalCorrect}/{totalQuestions} ({Math.round((totalCorrect / totalQuestions) * 100)}% Accuracy)
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="h-full bg-gray-300 rounded-xl flex items-center justify-center text-gray-500 shadow-md">
                <img 
                  src={case8Image} 
                  alt="MetroMove Transit Case" 
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              {isNotebookOpen && hasBeenOpened && (
                <div className="w-[300px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300 mt-4">
                  <div className="p-3 border-b border-gray-100">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <h3 className="text-base font-medium text-gray-700">Research Notes</h3>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          💡 Tip: Click power to collapse notebook & click floating to reopen the notebook wherever you are in the case
                        </p>
                      </div>
                      <button 
                        className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                          hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => hasStarted && handleNotebookToggle()}
                        disabled={!hasStarted}
                        title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                        </svg>
                        {hasStarted && (
                          <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                            <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                              Click to collapse notebook
                            </div>
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                  <div 
                    className="notebook-content h-[300px] bg-gray-50 p-3 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                    onDragOver={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.add('bg-blue-50');
                    }}
                    onDragLeave={(e) => {
                      if (!hasStarted) return;
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-blue-50');
                    }}
                    onDrop={handleDrop}
                    contentEditable={hasStarted}
                    suppressContentEditableWarning={true}
                    style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                  >
                    {!hasStarted && (
                      <div className="text-gray-500 text-sm italic">
                        Click "Start Case" to begin taking notes...
                      </div>
                    )}
                    {notebookItems.map((item, index) => (
                      <div 
                        key={index}
                        className="flex items-start gap-2 bg-white p-2 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                        contentEditable={false}
                      >
                        <div className="flex-1">
                          <div className="text-xs font-medium text-gray-900">{item.value}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5">{item.label}</div>
                        </div>
                        <button
                          onClick={() => hasStarted && removeNotebookItem(index)}
                          className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {hasStarted && (
              <div className="fixed top-[85px] right-10 md:right-20 z-50">
                <div 
                  className={`bg-white text-[15px] font-medium px-3 py-2 rounded-lg shadow-md flex items-center gap-1.5 ${
                    timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                      ? 'animate-pulse-dramatic border-2 border-red-500'
                      : ''
                  }`}
                >
                  <span className={`text-base ${
                    timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                      ? 'animate-bounce'
                      : ''
                  }`}>⌛</span>
                  <span className={`${
                    timeLeft <= (1200 * (1 - TIMER_WARNING_THRESHOLD)) && timeLeft > 0
                      ? 'text-red-500 font-bold'
                      : 'text-blue-600'
                  }`}>
                    Time Left: {formatTime(timeLeft)}
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* Problem Section - Enhanced design */}
          <div id="problem" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-12 bg-gray-50/80">
            <div className="absolute inset-x-0 -top-20 h-20 bg-white"></div>
            <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="md:col-span-2 relative group" style={{ 
                paddingRight: 'calc(460px + 1.25rem)',
                maxWidth: 'calc(100% - 100px)'
              }}>
                <div className="absolute -top-20 inset-x-0 bottom-0 bg-white rounded-2xl rounded-t-none shadow-xl transform group-hover:scale-[1.02] transition-transform duration-300">
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-blue-50/50 opacity-50" 
                       style={{ 
                         clipPath: 'polygon(0 25%, 100% 25%, 100% 100%, 0 100%)',
                         background: 'linear-gradient(to bottom, transparent 0%, rgba(59, 130, 246, 0.05) 50%, rgba(59, 130, 246, 0.1) 100%)'
                       }}>
                  </div>
                </div>
                <div className="relative p-8">
                  <h2 className="text-3xl font-bold mb-6 flex items-center gap-3 text-gray-900">
                    <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                      📝
                    </span>
                    Problem
                  </h2>
                  <div className="space-y-4 prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed">MetroMove is the public transportation agency for a growing metropolitan region. After several years of consistent population growth, ridership has increased across buses, trams, and light rail. However, infrastructure hasn't kept up evenly in all zones.</p>
                    <p className="text-gray-700 leading-relaxed">To plan a new phase of upgrades, MetroMove collected data from six city districts, including usage volume by transit type, delays, complaints, average commute time, and demographic transit preferences.</p>
                    <p className="text-gray-700 leading-relaxed">You've been hired to identify which zones would benefit most from investment and what modes of transport should be prioritized. The data includes some overlapping patterns, and your analysis needs to balance fairness, efficiency, and community impact.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Investigation Section */}
          <div id="investigation" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-12 bg-gradient-to-br from-blue-100/60 via-blue-50/40 to-white relative">
            <div className="container pl-[72px]">
              <h2 className="text-3xl font-bold mb-3 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  🔍
                </span>
                Investigation
              </h2>
              <p className="text-sm text-gray-600 mb-8 flex items-center gap-2">
                <span className="bg-blue-50 px-2 py-0.5 rounded">💡 Tip:</span>
                Drag and drop highlighted text into your notebook to collect important data points
              </p>
              <div className="flex gap-12 pr-[200px]">
                {/* Investigation Text */}
                <div className="w-[700px]">
                  <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative space-y-5">
                    <p className="text-lg leading-relaxed">
                      Exhibit 1 shows that <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "Central", "Highest ridership district")}
                      >Central</span> leads in overall ridership, with <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "9,400", "Central monthly rides")}
                      >9,400</span> monthly rides split between buses (<span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "4,100", "Central bus rides")}
                      >4,100</span>), trams (<span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "2,200", "Central tram rides")}
                      >2,200</span>), and rail (<span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "3,100", "Central rail rides")}
                      >3,100</span>). It also has the longest average commute at <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "41", "Central commute time (minutes)")}
                      >41</span> minutes (see Exhibit 2). Uptown follows with a balanced multimodal ridership.
                    </p>

                    <p className="text-lg leading-relaxed">
                      Complaint breakdown reveals buses account for <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "45%", "Bus complaints percentage")}
                      >45%</span> of all complaints, with light rail at <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "25%", "Light rail complaints percentage")}
                      >25%</span>. Tram services are most reliable, maintaining on-time performance above <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "90%", "Tram on-time performance")}
                      >90%</span> since March.
                    </p>

                    <p className="text-lg leading-relaxed">
                      Demographically, younger commuters (<span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "18–24", "Young commuters age range")}
                      >18–24</span>) prefer <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "buses", "Youth preferred transit")}
                      >buses</span>, while the <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "55+", "Senior commuters age range")}
                      >55+</span> group strongly prefers <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "Light Rail", "Senior preferred transit")}
                      >Light Rail</span>. Central is projected to grow <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "15%", "Central growth projection")}
                      >15%</span>, while <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "South", "Lowest growth district")}
                      >South</span> lags behind at <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "8%", "South growth projection")}
                      >8%</span>.
                    </p>

                    <p className="text-lg leading-relaxed">
                      Uptown recorded <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "1,900", "Uptown tram rides")}
                      >1,900</span> tram rides monthly. Light Rail had the <span 
                        className={`${hasStarted ? 'bg-blue-50 cursor-grab' : 'bg-gray-100 cursor-not-allowed'} px-2 py-1 rounded select-none`}
                        draggable={hasStarted}
                        onDragStart={(e) => hasStarted && handleDragStart(e, "lowest", "Light Rail February performance")}
                      >lowest</span> on-time performance in February. Peak delays are most common during weekday evenings in the East and South districts.
                    </p>
                  </div>
                </div>

                {/* Interactive Notebook */}
                {isNotebookOpen ? (
                  <div className="w-[500px] bg-white rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] overflow-hidden transition-all duration-300">
                    <div className="p-4 border-b border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-lg font-medium text-gray-700">Research Notes</h3>
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Tip: Click power to collapse notebook & click floating button to reopen the notebook wherever you are in the case
                          </p>
                        </div>
                        <button 
                          className={`text-gray-400 transition-colors duration-300 group relative p-1 ${
                            hasStarted ? 'hover:text-blue-600' : 'opacity-50 cursor-not-allowed'
                          }`}
                          onClick={() => hasStarted && handleNotebookToggle()}
                          disabled={!hasStarted}
                          title={hasStarted ? "Click to collapse notebook" : "Start case to enable notebook controls"}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v6m0 6v6M5.636 5.636l4.243 4.243m4.242 4.242l4.243 4.243M3 12h6m6 0h6M5.636 18.364l4.243-4.243m4.242-4.242l4.243-4.243" />
                          </svg>
                          {hasStarted && (
                            <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                              <div className="bg-gray-800/80 backdrop-blur-sm text-white text-[11px] rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                                Click to collapse notebook
                              </div>
                            </div>
                          )}
                        </button>
                      </div>
                    </div>
                    <div 
                      className="notebook-content h-[400px] bg-gray-50 p-4 overflow-y-auto shadow-[inset_0_2px_4px_rgba(0,0,0,0.06)]"
                      onDragOver={(e) => {
                        if (!hasStarted) return;
                        e.preventDefault();
                        e.currentTarget.classList.add('bg-blue-50');
                      }}
                      onDragLeave={(e) => {
                        if (!hasStarted) return;
                        e.preventDefault();
                        e.currentTarget.classList.remove('bg-blue-50');
                      }}
                      onDrop={handleDrop}
                      contentEditable={hasStarted}
                      suppressContentEditableWarning={true}
                      style={{ outline: 'none', cursor: hasStarted ? 'text' : 'not-allowed' }}
                    >
                      {!hasStarted && (
                        <div className="text-gray-500 text-sm italic">
                          Click "Start Case" to begin taking notes...
                        </div>
                      )}
                      {notebookItems.map((item, index) => (
                        <div 
                          key={index}
                          className="flex items-start gap-2 bg-white p-2.5 rounded shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] group mb-2"
                          contentEditable={false}
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{item.value}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{item.label}</div>
                          </div>
                          <button
                            onClick={() => hasStarted && removeNotebookItem(index)}
                            className={`${hasStarted ? 'opacity-0 group-hover:opacity-100' : 'hidden'} transition-opacity text-gray-400 hover:text-red-500`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={handleNotebookToggle}
                    className={`fixed right-24 top-1/2 -translate-y-1/2 bg-white p-4 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] transform transition-all duration-300 group z-[999] ${
                      hasStarted 
                        ? 'hover:shadow-[inset_0_2px_8px_rgba(0,0,0,0.15)] hover:scale-105'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    disabled={!hasStarted}
                  >
                    <div className="relative">
                      {hasStarted && (
                        <div className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full animate-pulse"></div>
                      )}
                      <svg 
                        className={`w-8 h-8 ${hasStarted ? 'text-gray-600 group-hover:text-blue-600' : 'text-gray-400'} transition-colors duration-300`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    {hasStarted && (
                      <div className="absolute right-full mr-2 top-1/2 -translate-y-1/2 pointer-events-none">
                        <div className="bg-gray-800/80 backdrop-blur-sm text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap shadow-lg">
                          Open notebook ({notebookItems.length} notes collected)
                        </div>
                      </div>
                    )}
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Exhibits Section */}
          <div id="exhibits" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-12 bg-gray-50/80">
            <div className="absolute inset-x-0 -top-20 h-20 bg-gray-50/80"></div>
            <div className="container pl-[72px]" style={{ 
                paddingRight: 'calc(460px + 1.25rem)',
                maxWidth: 'calc(100% - 100px)'
              }}>
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  📈
                </span>
                Exhibits
              </h2>
              <p className="text-sm text-gray-600 mb-4">Select an exhibit using the navigation below to analyze the data.</p>
              
              {/* Exhibit Navigation */}
              <div className="flex items-center gap-3 mb-4">
                {['1', '2', '3', '4', '5', '6'].map((exhibit) => (
                  <button
                    key={exhibit}
                    onClick={() => hasStarted && setCurrentExhibit(exhibit)}
                    disabled={!hasStarted}
                    className={`px-6 py-2 rounded-xl shadow-md transition-all duration-300 transform ${
                      !hasStarted 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50'
                        : currentExhibit === exhibit
                          ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white hover:shadow-lg hover:scale-105'
                          : 'bg-white text-gray-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105'
                    }`}
                  >
                    {exhibit}
                  </button>
                ))}
              </div>

              {/* Exhibit Display */}
              <div className={`bg-white rounded-xl shadow-sm transition-all duration-300 ${!hasStarted ? 'opacity-50' : 'hover:shadow-lg'}`}>
                <div className="p-4">
                  {currentExhibit === '1' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">📊</span>
                        Exhibit 1: Transit Usage by District and Mode
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={exhibit1Data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="district" />
                          <YAxis label={{ value: 'Monthly Rides', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="Bus" stackId="a" fill="#90cdf4" name="Bus" />
                          <Bar dataKey="Tram" stackId="a" fill="#f6ad55" name="Tram" />
                          <Bar dataKey="LightRail" stackId="a" fill="#228B22" name="Light Rail" />
                          <LabelList dataKey="Bus" position="top" />
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {currentExhibit === '2' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">🚌</span>
                        Exhibit 2: Average Commute Time by Zone
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={exhibit2Data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="zone" />
                          <YAxis label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="Minutes" fill="#800080" name="Commute Time">
                            <LabelList dataKey="Minutes" position="top" />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {currentExhibit === '3' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">📝</span>
                        Exhibit 3: Transit Complaint Distribution by Mode
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <PieChart>
                          <Pie data={exhibit3Data} dataKey="value" nameKey="mode" cx="50%" cy="50%" outerRadius={100} label>
                            {exhibit3Data.map((entry, idx) => (
                              <Cell key={`cell-${idx}`} fill={exhibit3Colors[idx]} />
                            ))}
                          </Pie>
                          <RechartsTooltip content={exhibit3Tooltip} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {currentExhibit === '4' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">⏱️</span>
                        Exhibit 4: On-Time Performance by Transit Mode
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <LineChart data={exhibit4Data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" />
                          <YAxis label={{ value: '% On-Time', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip />
                          <Legend />
                          <Line type="monotone" dataKey="Bus" stroke="#1e40af" name="Bus" activeDot={{ r: 8 }} />
                          <Line type="monotone" dataKey="Tram" stroke="#228B22" name="Tram" activeDot={{ r: 8 }} />
                          <Line type="monotone" dataKey="LightRail" stroke="#e11d48" name="Light Rail" activeDot={{ r: 8 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {currentExhibit === '5' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">🔵</span>
                        Exhibit 5: Transit Preference by Age Group
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <ScatterChart>
                          <CartesianGrid />
                          <XAxis dataKey="age" type="category" domain={['18–24', '25–34', '35–44', '45–54', '55+']} allowDuplicatedCategory={false} />
                          <YAxis label={{ value: 'Preference Score', angle: -90, position: 'insideLeft' }} />
                          <ZAxis range={[100, 400]} />
                          <RechartsTooltip />
                          <Legend />
                          <Scatter name="Bus" data={exhibit5Data} fill="#2563eb" line shape="circle" dataKey="Bus" />
                          <Scatter name="Tram" data={exhibit5Data} fill="#f59e42" line shape="circle" dataKey="Tram" />
                          <Scatter name="Light Rail" data={exhibit5Data} fill="#22c55e" line shape="circle" dataKey="LightRail" />
                        </ScatterChart>
                      </ResponsiveContainer>
                    </>
                  )}

                  {currentExhibit === '6' && (
                    <>
                      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <span className="text-blue-600">📈</span>
                        Exhibit 6: Projected Ridership Growth by District
                        {!hasStarted && (
                          <span className="text-xs text-gray-400 font-normal ml-2">(Start case to interact)</span>
                        )}
                      </h3>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart data={exhibit6Data} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="district" />
                          <YAxis label={{ value: 'Growth %', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="Growth" fill="#0891b2" name="Growth %">
                            <LabelList dataKey="Growth" position="top" formatter={(v) => `${v}%`} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Analysis Section */}
          <div id="questions" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-12 bg-gray-50/80">
            <div className="container pl-[72px]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  🧠
                </span>
                Analysis
              </h2>
              <div className="space-y-6 md:pr-[460px]">
                {questionData.map((q) => (
                  <div 
                    key={q.id} 
                    className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white font-medium shadow-md">
                        {q.id}
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-lg font-medium transition-all duration-300 hover:font-bold hover:text-blue-600 mb-4">
                          {q.question}
                        </h3>
                        {q.type === 'text' ? (
                          <div className="space-y-3">
                            <div className="flex gap-3">
                              <input
                                type="text"
                                value={answers[q.id] || ''}
                                onChange={(e) => handleInput(q.id, e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                className="flex-grow px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                placeholder="Type your answer and press Enter..."
                                disabled={!hasStarted || timeExpired || finishedEarly}
                              />
                              <button
                                onClick={() => handleSubmit(q.id)}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                disabled={!hasStarted || timeExpired || finishedEarly}
                              >
                                Submit
                              </button>
                            </div>
                            <div className="flex gap-2">
                              {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                                <button
                                  onClick={() => handleTryAgain(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  Try Again
                                </button>
                              )}
                              {(timeExpired || finishedEarly) && (
                                <button
                                  onClick={() => handleReveal(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  See Answer
                                </button>
                              )}
                            </div>
                            {feedback[q.id] && (
                              <div className={`mt-4 p-3 rounded-lg ${
                                feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                              }`}>
                                {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                              </div>
                            )}
                            {reveal[q.id] && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                              </div>
                            )}
                          </div>
                        ) : q.type === 'multi' ? (
                          <div className="space-y-3">
                            <div className="flex flex-col gap-3">
                              {q.parts.map((part, index) => (
                                <div key={index} className="flex gap-3">
                                  <div className="flex-grow">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{part.label}</label>
                                    <input
                                      type="text"
                                      value={((answers[q.id] || {})[index] || '')}
                                      onChange={(e) => handleInput(q.id, e.target.value, index)}
                                      onKeyDown={(e) => e.key === 'Enter' && handleSubmit(q.id)}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 hover:border-blue-400"
                                      placeholder={`Enter ${part.label.toLowerCase()}...`}
                                      disabled={!hasStarted || timeExpired || finishedEarly}
                                    />
                                  </div>
                                </div>
                              ))}
                              <button
                                onClick={() => handleSubmit(q.id)}
                                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                disabled={!hasStarted || timeExpired || finishedEarly}
                              >
                                Submit
                              </button>
                            </div>
                            <div className="flex gap-2">
                              {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                                <button
                                  onClick={() => handleTryAgain(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  Try Again
                                </button>
                              )}
                              {(timeExpired || finishedEarly) && (
                                <button
                                  onClick={() => handleReveal(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  See Answer
                                </button>
                              )}
                            </div>
                            {feedback[q.id] && (
                              <div className={`mt-4 p-3 rounded-lg ${
                                feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                              }`}>
                                {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                              </div>
                            )}
                            {reveal[q.id] && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                <p className="text-blue-700">
                                  🔍 Answer: 
                                  {q.parts.map((part, index) => (
                                    <span key={index}>
                                      <br />
                                      <strong>{part.label}</strong>: {part.correctAnswer}
                                    </span>
                                  ))}
                                </p>
                              </div>
                            )}
                          </div>
                        ) : q.type === 'mcq' ? (
                          <div className="space-y-2">
                            {q.options.map((option) => (
                              <button
                                key={option}
                                onClick={() => handleMCQSelect(q.id, option)}
                                className={`w-full text-left px-4 py-2 rounded-lg border transition-all duration-300 transform hover:scale-102 ${
                                  answers[q.id] === option
                                    ? 'bg-gradient-to-r from-blue-600 to-blue-400 text-white border-transparent'
                                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                                }`}
                                disabled={!hasStarted || timeExpired || finishedEarly}
                              >
                                {option}
                              </button>
                            ))}
                            <div className="flex gap-2 mt-3">
                              {feedback[q.id] === 'incorrect' && hasStarted && !timeExpired && !finishedEarly && (
                                <button
                                  onClick={() => handleTryAgain(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-gray-600 to-gray-500 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  Try Again
                                </button>
                              )}
                              {(timeExpired || finishedEarly) && (
                                <button
                                  onClick={() => handleReveal(q.id)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                                >
                                  See Answer
                                </button>
                              )}
                            </div>
                            {feedback[q.id] && (
                              <div className={`mt-4 p-3 rounded-lg ${
                                feedback[q.id] === 'correct' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                              }`}>
                                {feedback[q.id] === 'correct' ? 'Correct!' : 'Incorrect. Try again.'}
                              </div>
                            )}
                            {reveal[q.id] && (
                              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                                <p className="text-blue-700">🔍 Answer: <strong>{q.correctAnswer}</strong></p>
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}

                {finishedEarly && (
                  <div className="mt-6 text-green-600 text-base font-semibold">
                    🎉 Congratulations! You completed all questions before the timer ran out! Amazing work!
                  </div>
                )}
              </div>
            </div>
          </section>

          <div id="report" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-20 bg-gray-50/80">
            <div className="container pl-[72px]">
              <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                  📊
                </span>
                Report
              </h2>
              <div className="space-y-6 md:pr-[460px]">
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 group relative">
                  <div className="flex items-start gap-4">
                    <div className="flex-grow">
                      <div className="space-y-3">
                        <div className="text-lg leading-relaxed text-justify" style={{ lineHeight: '2' }}>
                          MetroMove's data confirms that <input
                            type="text"
                            value={answers['report_district'] || ''}
                            onChange={(e) => handleInput('report_district', e.target.value)}
                            className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_district'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_district'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          /> has the highest overall ridership and the longest commute at <input
                            type="text"
                            value={answers['report_commute'] || ''}
                            onChange={(e) => handleInput('report_commute', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_commute'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_commute'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          /> minutes. <input
                            type="text"
                            value={answers['report_mode'] || ''}
                            onChange={(e) => handleInput('report_mode', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_mode'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_mode'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          /> is the most reliable mode with over <input
                            type="text"
                            value={answers['report_ontime'] || ''}
                            onChange={(e) => handleInput('report_ontime', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_ontime'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_ontime'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          />% on-time rate in March, while Light Rail remains least consistent.

                          Complaint rates are highest for <input
                            type="text"
                            value={answers['report_transport'] || ''}
                            onChange={(e) => handleInput('report_transport', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_transport'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_transport'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          />, accounting for <input
                            type="text"
                            value={answers['report_complaints'] || ''}
                            onChange={(e) => handleInput('report_complaints', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_complaints'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_complaints'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          />% of all transit complaints. <input
                            type="text"
                            value={answers['report_district2'] || ''}
                            onChange={(e) => handleInput('report_district2', e.target.value)}
                            className={`w-32 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_district2'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_district2'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          /> is expected to grow <input
                            type="text"
                            value={answers['report_growth'] || ''}
                            onChange={(e) => handleInput('report_growth', e.target.value)}
                            className={`w-24 mx-2 px-4 py-2 border-2 rounded-md focus:outline-none focus:ring-2 transition-all duration-300 text-lg ${
                              (timeExpired || finishedEarly) && feedback['report_growth'] === 'correct' ? 'border-green-500 bg-green-50 ring-green-200' :
                              (timeExpired || finishedEarly) && feedback['report_growth'] === 'incorrect' ? 'border-red-500 bg-red-50 ring-red-200' :
                              'border-gray-300 focus:ring-blue-200'
                            }`}
                            disabled={!hasStarted || timeExpired || finishedEarly}
                            onKeyDown={(e) => e.key === 'Enter' && handleReportSubmit()}
                          />%, justifying infrastructure expansion.
                        </div>
                        <div className="flex gap-2">
                          {(!timeExpired && !finishedEarly) && (
                            <button
                              onClick={() => handleReportSubmit()}
                              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                              disabled={!hasStarted || timeExpired || finishedEarly}
                            >
                              Submit Answers
                            </button>
                          )}
                          {(timeExpired || finishedEarly) && (
                            <button
                              onClick={() => handleReportReveal()}
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-400 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                            >
                              See Answers
                            </button>
                          )}
                        </div>
                        {(timeExpired || finishedEarly) && feedback['report'] && (
                          <div className={`mt-4 p-3 rounded-lg ${
                            Object.values(feedback).every(f => f === 'correct') 
                              ? 'bg-green-50 text-green-700' 
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {Object.values(feedback).every(f => f === 'correct') 
                              ? 'All answers are correct!' 
                              : 'Some answers are incorrect. Please check your answers.'}
                          </div>
                        )}
                        {reveal['report'] && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-700">
                              🔍 Answers:<br/>
                              Central (highest ridership district)<br/>
                              41 minutes (longest commute)<br/>
                              Tram (most reliable mode)<br/>
                              90% (on-time rate)<br/>
                              Bus (highest complaints)<br/>
                              45% (of all complaints)<br/>
                              Central (growth district)<br/>
                              15% (growth percentage)
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {finishedEarly && (
                  <div className="mt-6 text-green-600 text-base font-semibold">
                    🎉 Congratulations! You completed the report before the timer ran out! Amazing work!
                  </div>
                )}
                {timeExpired && !finishedEarly && (
                  <div className="mt-6 text-red-600 text-base font-semibold">
                    ❌ Time's up! You can still view the answers by clicking "See Answers".
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Recommendation Section */}
          <div id="recommendation" className="block h-0 -mt-[400px] pt-[400px]"></div>
          <section className="py-20 bg-gradient-to-br from-blue-50/40 via-blue-50/20 to-white">
            <div className="container pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="md:col-span-2 md:pr-[460px]">
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-100 to-white rounded-lg shadow-inner">
                    💡
                  </span>
                  Recommendation
                </h2>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="mr-3 text-xl">📊</span>
                    <span>Improve rail reliability and modernize bus fleets in Central</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-xl">👥</span>
                    <span>Expand tram routes in high-growth areas like Central</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-xl">🎯</span>
                    <span>Align infrastructure investments with demographic preferences</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3 text-xl">♻️</span>
                    <span>Reduce bus complaint rates through service improvements</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default Case8Page; 

<style jsx global>{`
    @keyframes pulse-dramatic {
      0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
      }
      50% {
        transform: scale(1.15);
        box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
      }
    }
    
    .animate-pulse-dramatic {
      animation: pulse-dramatic 1s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    }

    @keyframes bounce {
      0%, 100% {
        transform: translateY(0) scale(1);
      }
      50% {
        transform: translateY(-5px) scale(1.2);
      }
    }

    .animate-bounce {
      animation: bounce 0.5s ease infinite;
    }
  `}</style>